import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import JsonLd from '../../components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://workplaysave.com';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/7965406aa664502cb6a3888418e2a788.jpg';

interface PolicySection {
  number: string;
  icon: string;
  title: string;
  accentColor: string;
  borderColor: string;
  iconBg: string;
  content: React.ReactNode;
}

const sections: PolicySection[] = [
  {
    number: '01',
    icon: 'ri-focus-3-line',
    title: 'Our Mission',
    accentColor: 'text-cyan-400',
    borderColor: 'border-cyan-500/20',
    iconBg: 'bg-cyan-600/15',
    content: (
      <div className="space-y-4 text-gray-300 leading-relaxed">
        <p>
          Work Play Save exists to help people find the best technology for gaming, streaming, and everyday productivity, without wasting money on gear that doesn't live up to the hype.
        </p>
        <p>
          We believe great tech shouldn't require endless research or a premium budget. Our goal is to cut through the noise, surface the products that genuinely deliver value, and present our findings clearly, honestly, and without bias.
        </p>
        <div className="flex items-start gap-3 p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/15">
          <div className="w-5 h-5 flex items-center justify-center text-cyan-400 flex-shrink-0 mt-0.5">
            <i className="ri-double-quotes-l text-lg"></i>
          </div>
          <p className="text-gray-300 italic text-sm">
            "Research-backed recommendations to upgrade your setup without overspending. No fluff, no bias. Just the gear that's actually worth buying."
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '02',
    icon: 'ri-search-eye-line',
    title: 'How We Choose Products',
    accentColor: 'text-purple-400',
    borderColor: 'border-purple-500/20',
    iconBg: 'bg-purple-600/15',
    content: (
      <div className="space-y-4 text-gray-300 leading-relaxed">
        <p>
          Every product recommendation on Work Play Save goes through a consistent evaluation process. We don't just list popular products. We research why they're worth your money.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          {[
            { icon: 'ri-bar-chart-2-line', color: 'text-purple-400', title: 'Performance Research', desc: 'We analyse real-world benchmarks, user reviews, and expert assessments to understand how each product performs under genuine conditions.' },
            { icon: 'ri-group-line', color: 'text-purple-400', title: 'User Feedback', desc: 'Thousands of verified Amazon buyer reviews are studied for recurring praise and complaints across different use cases.' },
            { icon: 'ri-scales-3-line', color: 'text-purple-400', title: 'Value for Money', desc: 'We compare each product against its closest competitors at similar price points, prioritising value over brand recognition.' },
            { icon: 'ri-tools-line', color: 'text-purple-400', title: 'Real-World Use', desc: 'We consider how each product performs in typical home, gaming, and office environments, not just controlled lab conditions.' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-purple-500/5 border border-purple-500/10">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${item.icon} ${item.color} text-base`}></i>
              </div>
              <div>
                <p className="text-white font-semibold text-sm mb-1">{item.title}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-400 pt-2">
          We focus exclusively on products that are currently available and in stock on Amazon, so every recommendation is actionable.
        </p>
      </div>
    ),
  },
  {
    number: '03',
    icon: 'ri-link-m',
    title: 'Affiliate Disclosure',
    accentColor: 'text-amber-400',
    borderColor: 'border-amber-500/20',
    iconBg: 'bg-amber-600/15',
    content: (
      <div className="space-y-4 text-gray-300 leading-relaxed">
        <p>
          Work Play Save participates in the Amazon Associates affiliate programme and may earn commissions from qualifying purchases made through links on this site.
        </p>
        <div className="space-y-3">
          {[
            { icon: 'ri-checkbox-circle-fill', color: 'text-emerald-400', text: 'Commissions come from Amazon\'s marketing budget, not from any price increase on your end. You pay exactly the same price.' },
            { icon: 'ri-checkbox-circle-fill', color: 'text-emerald-400', text: 'These commissions fund the site and allow us to keep publishing free, independent buying guides.' },
            { icon: 'ri-checkbox-circle-fill', color: 'text-emerald-400', text: 'Affiliate relationships have zero influence on which products we recommend or how we rank them.' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${item.icon} ${item.color} text-base`}></i>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-400">
          For full details, see our{' '}
          <Link to="/affiliate-disclosure" className="text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors duration-200">
            Affiliate Disclosure page
          </Link>.
        </p>
      </div>
    ),
  },
  {
    number: '04',
    icon: 'ri-shield-check-line',
    title: 'No Sponsored Bias',
    accentColor: 'text-emerald-400',
    borderColor: 'border-emerald-500/20',
    iconBg: 'bg-emerald-600/15',
    content: (
      <div className="space-y-4 text-gray-300 leading-relaxed">
        <p>
          We do not accept payment, free products, or any form of compensation in exchange for favourable coverage or higher rankings in our guides.
        </p>
        <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/15 space-y-3">
          <p className="text-emerald-400 font-semibold text-sm uppercase tracking-wide">Our Editorial Commitments</p>
          {[
            'No brand pays to be included in our buying guides',
            'No product receives a higher ranking because of commercial relationships',
            'Negative findings are reported alongside positives, with honest pros and cons for every product',
            'If a recommendation changes due to new information, we update the article',
          ].map((point, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-arrow-right-s-line text-emerald-400 text-base"></i>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">{point}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-400">
          Our rankings are based purely on merit: performance, value, reliability, and real-world usefulness.
        </p>
      </div>
    ),
  },
  {
    number: '05',
    icon: 'ri-refresh-line',
    title: 'Accuracy & Updates',
    accentColor: 'text-cyan-400',
    borderColor: 'border-cyan-500/20',
    iconBg: 'bg-cyan-600/15',
    content: (
      <div className="space-y-4 text-gray-300 leading-relaxed">
        <p>
          The tech market moves fast. Products go out of stock, get discontinued, receive price changes, or get replaced by better alternatives. We take content accuracy seriously.
        </p>
        <div className="space-y-3">
          {[
            { icon: 'ri-calendar-check-line', color: 'text-cyan-400', title: 'Regular Reviews', desc: 'Buying guides are reviewed and updated periodically to ensure product availability, relevance, and accuracy.' },
            { icon: 'ri-links-line', color: 'text-cyan-400', title: 'Link Verification', desc: 'Affiliate links are checked regularly to ensure they point to the correct, available product listings on Amazon.' },
            { icon: 'ri-price-tag-3-line', color: 'text-cyan-400', title: 'Pricing Transparency', desc: 'We do not display static prices in our articles because prices fluctuate. We always direct you to Amazon for the current price.' },
            { icon: 'ri-time-line', color: 'text-cyan-400', title: 'Publish Dates', desc: 'Every article includes its publish date and is marked with the year it was last reviewed, so you always know how current the information is.' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-base">
                <i className={`${item.icon} ${item.color}`}></i>
              </div>
              <div>
                <p className="text-white font-semibold text-sm mb-0.5">{item.title}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    number: '06',
    icon: 'ri-eye-line',
    title: 'Transparency',
    accentColor: 'text-purple-400',
    borderColor: 'border-purple-500/20',
    iconBg: 'bg-purple-600/15',
    content: (
      <div className="space-y-4 text-gray-300 leading-relaxed">
        <p>
          We believe you deserve to know exactly how a recommendation is formed. Every product guide on Work Play Save is structured to give you the full picture.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mt-2">
          {[
            { icon: 'ri-thumb-up-line', color: 'text-emerald-400', bg: 'bg-emerald-500/8 border-emerald-500/15', label: 'Honest Pros', desc: 'Clear reasons why a product is worth buying' },
            { icon: 'ri-thumb-down-line', color: 'text-red-400', bg: 'bg-red-500/8 border-red-500/15', label: 'Genuine Cons', desc: 'Real limitations you should know before buying' },
            { icon: 'ri-equalizer-line', color: 'text-purple-400', bg: 'bg-purple-500/8 border-purple-500/15', label: 'Comparisons', desc: 'Side-by-side tables to help you decide' },
          ].map((item, i) => (
            <div key={i} className={`flex flex-col items-center text-center p-4 rounded-xl border ${item.bg} gap-2`}>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${item.icon} ${item.color} text-xl`}></i>
              </div>
              <p className="text-white font-semibold text-sm">{item.label}</p>
              <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-400 pt-2">
          If you have questions about a specific recommendation or believe information is inaccurate, you can reach us through the{' '}
          <Link to="/about" className="text-purple-400 hover:text-purple-300 underline underline-offset-2 transition-colors duration-200">
            About page
          </Link>.
          We take feedback seriously and will investigate and correct errors promptly.
        </p>
      </div>
    ),
  },
];

export default function ReviewPolicyPage() {
  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Review Policy | Work Play Save',
    description: 'Read our review policy to understand how we test, evaluate, and recommend products to ensure honest and unbiased tech reviews.',
    url: `${SITE_URL}/review-policy`,
    publisher: {
      '@type': 'Organization',
      name: 'Work Play Save',
      url: SITE_URL,
    },
    dateModified: '2026-03-18',
  }), []);

  return (
    <>
      <title>Review Policy | Work Play Save</title>
      <meta name="description" content="Read our review policy to understand how we test, evaluate, and recommend products to ensure honest and unbiased tech reviews." />
      <meta name="keywords" content="review policy, product testing process, unbiased reviews, tech review standards, product evaluation, honest reviews policy" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/review-policy`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Review Policy | Work Play Save" />
      <meta property="og:description" content="Read our review policy to understand how we test, evaluate, and recommend products to ensure honest and unbiased tech reviews." />
      <meta property="og:url" content={`${SITE_URL}/review-policy`} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:site_name" content="Work Play Save" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Review Policy | Work Play Save" />
      <meta name="twitter:description" content="Read our review policy to understand how we test, evaluate, and recommend products to ensure honest and unbiased tech reviews." />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <JsonLd data={webPageSchema} />
      <div className="min-h-screen">
        {/* Hero Header */}
        <header className="relative overflow-hidden bg-gradient-to-b from-gray-900 to-black border-b border-gray-800">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-purple-600/8 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/3 w-64 h-64 bg-cyan-500/6 rounded-full blur-3xl" />
          </div>
          <div className="max-w-4xl mx-auto px-6 py-20 relative z-10">
            <div className="text-center space-y-5">
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-purple-500/30 rounded-full text-xs uppercase tracking-widest text-purple-400 bg-purple-500/5 backdrop-blur-sm">
                <i className="ri-file-text-line text-purple-400"></i>
                Transparency &amp; Trust
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold text-white uppercase tracking-tight">
                Review Policy
                <div className="h-1 w-32 bg-gradient-to-r from-transparent via-purple-500 to-transparent mx-auto mt-4 rounded-full"></div>
              </h1>
              <p className="text-gray-400 text-lg max-w-xl mx-auto leading-relaxed">
                How we research, evaluate, and recommend the best tech for gaming, streaming, and productivity.
              </p>
              <p className="text-sm text-gray-600">
                Last updated: March 18, 2026
              </p>
            </div>
          </div>
        </header>

        {/* Intro summary strip */}
        <div className="bg-gradient-to-r from-purple-900/15 via-cyan-900/10 to-emerald-900/15 border-b border-gray-800/60">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
              {[
                { icon: 'ri-shield-check-fill', color: 'text-emerald-400', text: 'No paid promotions' },
                { icon: 'ri-award-fill', color: 'text-amber-400', text: 'Merit-based rankings only' },
                { icon: 'ri-refresh-fill', color: 'text-cyan-400', text: 'Regularly updated content' },
                { icon: 'ri-price-tag-3-fill', color: 'text-purple-400', text: 'No static pricing' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} ${item.color} text-sm`}></i>
                  </div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="space-y-8">
            {sections.map((section) => (
              <section
                key={section.number}
                className={`rounded-2xl border ${section.borderColor} bg-gradient-to-br from-white/[0.02] to-transparent overflow-hidden`}
              >
                {/* Section header */}
                <div className="flex items-center gap-4 px-8 py-6 border-b border-white/5">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${section.iconBg} flex-shrink-0`}>
                    <i className={`${section.icon} ${section.accentColor} text-lg`}></i>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-widest">{section.number}</span>
                    <h2 className={`text-xl sm:text-2xl font-bold text-white`}>
                      {section.title}
                    </h2>
                  </div>
                </div>

                {/* Section body */}
                <div className="px-8 py-7">
                  {section.content}
                </div>
              </section>
            ))}
          </div>

          {/* Bottom divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-14" />

          {/* CTA footer block */}
          <div className="text-center space-y-4">
            <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
              Have a question about how we reviewed a specific product? Noticed something inaccurate? We want to hear from you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/about"
                className="px-7 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg font-bold text-sm hover:shadow-xl hover:shadow-purple-500/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </Link>
              <Link
                to="/affiliate-disclosure"
                className="px-7 py-3 border border-white/20 text-gray-300 hover:text-white hover:border-white/40 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Affiliate Disclosure
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
